import type { DemoSession, DemoStageId } from './demoExperience';

export type AdventureTrait = 'steady' | 'explorer' | 'sharing' | 'analyst';

export interface AdventureChoice {
  label: string;
  detail: string;
  trait: AdventureTrait;
  worldEffect: string;
  collectible: string;
  asset?: string;
  secret?: boolean;
}

export interface AdventureResult {
  dominantTrait: AdventureTrait;
  resultTitle: string;
  certificateTitle: string;
  resultCopy: string;
  snowsLine: string;
  traitScores: Record<AdventureTrait, number>;
  worldEffects: string[];
  collectibles: string[];
  hiddenBadgeUnlocked: boolean;
  festivalFund: number;
}

const A = '/assets';

export const ADVENTURE_GOAL = {
  title: '오로라 축제 준비 작전',
  target: 10000,
  copy: '8개 공간에서 현명한 선택을 모아 오로라 축제의 가족 여행 자금을 완성해요.',
};

export const ADVENTURE_CHOICES: Record<DemoStageId, AdventureChoice[]> = {
  'first-snow': [
    { label: '안전하게 차근차근 모으기', detail: '작은 금액을 매주 꾸준히', trait: 'steady', worldEffect: '베이스캠프의 첫 등불이 켜졌어요.', collectible: '첫눈 나침반' },
    { label: '새로운 공간을 많이 탐험하기', detail: '미션을 찾아 적극적으로 이동', trait: 'explorer', worldEffect: '월드 지도에 새로운 길이 나타났어요.', collectible: '탐험 깃발' },
    { label: '가족과 함께 목표를 만들기', detail: '응원과 약속을 함께 기록', trait: 'sharing', worldEffect: '냥장고 집 창문에 따뜻한 불이 켜졌어요.', collectible: '가족 눈꽃' },
  ],
  'goal-road': [
    { label: '작게 나누어 꾸준히 모으기', detail: '매주 10,000원씩', trait: 'steady', worldEffect: '목표 스노우볼이 단단해졌어요.', collectible: '꾸준함 결정' },
    { label: '가격과 기간을 먼저 계산하기', detail: '필요한 주와 금액을 비교', trait: 'analyst', worldEffect: '관측소의 계산판이 밝아졌어요.', collectible: '계획 렌즈' },
    { label: '가족과 목표 이유를 이야기하기', detail: '목표의 의미를 함께 확인', trait: 'sharing', worldEffect: '가족 탐험대 깃발이 올라갔어요.', collectible: '약속 리본' },
  ],
  basecamp: [
    { label: '받았어요 · 5,000원', detail: '새로운 수입을 기록', trait: 'explorer', worldEffect: '베이스캠프 보급 상자가 채워졌어요.', collectible: '기록 스탬프', asset: `${A}/ui-icons/mrm_ui_03a_received_v01.png` },
    { label: '저축했어요 · 3,000원', detail: '목표 스노우볼에 추가', trait: 'steady', worldEffect: '스노우볼이 한 단계 커졌어요.', collectible: '저축 눈송이', asset: `${A}/ui-icons/mrm_ui_03c_saved_v01.png` },
    { label: '간식에 썼어요 · 2,000원', detail: '감정과 이유까지 기록', trait: 'analyst', worldEffect: '마켓 장부에 선택 이유가 기록됐어요.', collectible: '선택 메모', asset: `${A}/ui-icons/mrm_ui_04a_snack_v01.png` },
  ],
  weather: [
    { label: '가격은 여러 원인을 함께 살펴봐요', detail: '원재료·날씨·운송비를 관찰', trait: 'analyst', worldEffect: '뉴스 스퀘어의 경제 날씨가 맑아졌어요.', collectible: '경제 날씨 카드' },
    { label: '가격이 오르면 잠시 기다려 봐요', detail: '상황이 바뀌는지 관찰', trait: 'steady', worldEffect: '마켓의 충동 경고등이 꺼졌어요.', collectible: '기다림 타이머' },
    { label: '가족과 뉴스의 의미를 이야기해요', detail: '각자 다른 생각을 비교', trait: 'sharing', worldEffect: '뉴스 광장의 대화 종이 울렸어요.', collectible: '대화 마이크' },
  ],
  library: [
    { label: '두 자료의 날짜와 출처를 먼저 비교해요', detail: '가짜 페이지를 찾는 비밀 선택', trait: 'analyst', worldEffect: '경제도서관의 비밀문이 열렸어요.', collectible: '진실의 페이지', secret: true },
    { label: '은행이 돈을 연결하는 원리를 배워요', detail: '신뢰와 약속의 구조', trait: 'steady', worldEffect: '마법책의 은행 페이지가 복원됐어요.', collectible: '신뢰 책갈피' },
    { label: '배운 내용을 친구에게 설명해요', detail: '지식을 나누며 기억하기', trait: 'sharing', worldEffect: '도서관의 공유 램프가 켜졌어요.', collectible: '설명 별표' },
  ],
  fridge: [
    { label: '사고 싶은 것을 하루 기다린 기억', detail: '기다림 성공 카드', trait: 'steady', worldEffect: '냥장고에 기다림 카드가 보관됐어요.', collectible: '기다림 카드' },
    { label: '틀린 퀴즈를 다시 풀어 맞힌 기억', detail: '재도전 별 카드', trait: 'explorer', worldEffect: '냥장고의 재도전 칸이 반짝였어요.', collectible: '재도전 별' },
    { label: '가족과 함께 웃었던 대화 기억', detail: '응원 메시지와 함께 보관', trait: 'sharing', worldEffect: '냥장고 집의 가족 사진이 빛났어요.', collectible: '응원 사진' },
  ],
  family: [
    { label: '가족 여행 계획을 함께 세우기', detail: '목표를 대화로 연결', trait: 'sharing', worldEffect: '북극 항구에 가족 탐험선이 도착했어요.', collectible: '가족 승선권' },
    { label: '시장 가격을 직접 비교해 보기', detail: '현실 금융 탐험', trait: 'analyst', worldEffect: '항구의 물류 지도가 완성됐어요.', collectible: '가격 지도' },
    { label: '새로운 장소에서 금융 미션 찾기', detail: '패스포트 탐험', trait: 'explorer', worldEffect: '북극 항구의 새 항로가 열렸어요.', collectible: '항구 패스포트' },
  ],
  summit: [
    { label: '다음 달 작은 정상 정하기', detail: '새 목표로 이어가기', trait: 'steady', worldEffect: '산 정상에 다음 목표 깃발이 꽂혔어요.', collectible: '다음 정상 깃발' },
    { label: '새로운 경제 퀴즈 제안하기', detail: '배운 것을 다른 사람에게 전하기', trait: 'explorer', worldEffect: '오로라 관측소에 새 탐험 신호가 생겼어요.', collectible: '퀴즈 발명 배지' },
    { label: '나의 경험을 가족에게 이야기하기', detail: '회고와 연결', trait: 'sharing', worldEffect: '오로라 축제의 가족 무대가 열렸어요.', collectible: '이야기 마이크' },
  ],
};

const TRAIT_META: Record<AdventureTrait, Omit<AdventureResult, 'dominantTrait' | 'traitScores' | 'worldEffects' | 'collectibles' | 'hiddenBadgeUnlocked' | 'festivalFund'>> = {
  steady: {
    resultTitle: '차분한 설계자',
    certificateTitle: '꾸준한 선택 설계자 수료증',
    resultCopy: '작은 행동을 나누고 기다리며 목표를 안정적으로 키우는 힘이 가장 선명했어요.',
    snowsLine: '급하게 굴리지 않아도 괜찮아. 네 스노우볼은 아주 단단해!',
  },
  explorer: {
    resultTitle: '용감한 탐험가',
    certificateTitle: '용감한 금융 탐험가 수료증',
    resultCopy: '새로운 공간과 미션에 도전하며 배운 것을 행동으로 옮기는 힘이 돋보였어요.',
    snowsLine: '새로운 길을 발견할 때마다 월드가 더 넓어졌어!',
  },
  sharing: {
    resultTitle: '따뜻한 연결자',
    certificateTitle: '따뜻한 가족 연결자 수료증',
    resultCopy: '돈의 선택을 가족의 대화와 응원으로 연결하는 힘이 가장 크게 자랐어요.',
    snowsLine: '함께 굴린 스노우볼은 오래 기억되는 눈길이 돼!',
  },
  analyst: {
    resultTitle: '경제 관찰자',
    certificateTitle: '경제의 날씨를 읽는 관찰자 수료증',
    resultCopy: '숫자와 정보의 이유를 비교하고, 서두르지 않고 판단하는 힘이 돋보였어요.',
    snowsLine: '보이는 숫자 뒤의 이유까지 찾아낸 건 정말 멋진 일이야!',
  },
};

const TRAIT_PRIORITY: AdventureTrait[] = ['steady', 'explorer', 'sharing', 'analyst'];

export function deriveAdventureResult(session: Pick<DemoSession, 'selections' | 'completedStageIds' | 'points'>): AdventureResult {
  const traitScores: Record<AdventureTrait, number> = { steady: 0, explorer: 0, sharing: 0, analyst: 0 };
  const worldEffects: string[] = [];
  const collectibles: string[] = [];
  let hiddenBadgeUnlocked = false;

  for (const stageId of session.completedStageIds) {
    const selected = session.selections[stageId];
    const choice = ADVENTURE_CHOICES[stageId].find((item) => item.label === selected);
    if (!choice) continue;
    traitScores[choice.trait] += 1;
    worldEffects.push(choice.worldEffect);
    collectibles.push(choice.collectible);
    hiddenBadgeUnlocked ||= Boolean(choice.secret);
  }

  const dominantTrait = TRAIT_PRIORITY.reduce((best, trait) => traitScores[trait] > traitScores[best] ? trait : best, TRAIT_PRIORITY[0]);
  const meta = TRAIT_META[dominantTrait];
  const completionBonus = session.completedStageIds.length * 525;
  const festivalFund = Math.min(ADVENTURE_GOAL.target, session.points * 10 + completionBonus);

  return {
    dominantTrait,
    ...meta,
    traitScores,
    worldEffects,
    collectibles,
    hiddenBadgeUnlocked,
    festivalFund,
  };
}
